<template>
  <!-- One order-wide choice, not a second way to buy: ticking it only records
       that the order is a gift. The pair still goes into the bag through
       Add to cart (or the sticky bar, or a wallet), and every one of those
       paths reads this same preference. It used to add the pair and open the
       bag by itself, which surprised shoppers who were only choosing
       packaging. The label matches the shopping bag and checkout controls
       bound to the same preference, so the shopper meets the same words
       ticked on the next screen. -->
  <div class="product-gift" :class="{ 'product-gift--selected': isGiftOrder }">
    <label class="product-gift__choice">
      <input
        type="checkbox"
        :checked="isGiftOrder"
        :disabled="unavailable"
        :aria-describedby="hintId"
        @change="onChange"
      />
      <svg
        class="product-gift__icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.4"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M4 10h16v11H4zM3 6h18v4H3zM12 6v15" />
        <path
          d="M12 6H8.5A2.5 2.5 0 1 1 11 3.5L12 6Zm0 0h3.5A2.5 2.5 0 1 0 13 3.5L12 6Z"
        />
      </svg>
      <span>Make it a gift</span>
    </label>
    <!-- Outside the label so the control's name stays "Make it a gift". It
         says what the option is before the tick, the same line the bag and
         checkout show, and confirms it after. -->
    <p :id="hintId" class="product-gift__hint">
      {{ isGiftOrder ? GIFT_CONFIRMATION : GIFT_HINT }}
    </p>
    <!-- Announces only a change the shopper made. The preference is read
         after mount, so a live region bound to it would speak on every page
         load while a gift order is already on. -->
    <span class="sr-only" role="status">{{ announcement }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useId, watch } from "vue";
import { useGiftOrderPreference } from "../../../../../utils/giftOrder";

const GIFT_HINT = "Thoughtfully wrapped, ready to give.";
const GIFT_CONFIRMATION = "Your order will be wrapped as a gift.";

const props = defineProps({
  product: { type: Object, default: () => null },
});

const hintId = `product-gift-hint-${useId()}`;
const { isGiftOrder, setIsGiftOrder } = useGiftOrderPreference();
const announcement = ref("");

const unavailable = computed(
  () => !props.product?.stock || !!props.product?.__productPreview
);

// The value this control has just written, so the watcher below can tell its
// own change from one made by the bag or checkout control.
let ownValue: boolean | null = null;

// A change made elsewhere empties the region: otherwise it would keep saying
// the order will be wrapped after the bag's checkbox has turned that off.
watch(isGiftOrder, (value) => {
  if (value !== ownValue) announcement.value = "";
  ownValue = null;
});

async function onChange(event: Event) {
  const checkbox = event.target as HTMLInputElement;
  if (unavailable.value) {
    checkbox.checked = isGiftOrder.value;
    return;
  }
  ownValue = checkbox.checked;
  setIsGiftOrder(checkbox.checked);
  // Cleared first so ticking again after an untick is announced again.
  announcement.value = "";
  if (!checkbox.checked) return;
  await nextTick();
  announcement.value = GIFT_CONFIRMATION;
}
</script>

<style scoped lang="scss">
// A checkbox row with no box around it: in the purchase column a border or a
// fill means a way to pay, and this is an option on the order.
.product-gift {
  color: #101820;
}

.product-gift__choice {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  input {
    width: 20px;
    height: 20px;
    margin: 0;
    flex-shrink: 0;
    // The forms plugin paints a checked box with `currentColor` (its default
    // is blue), so accent-color alone never reached the screen.
    color: #101820;
    accent-color: #101820;
    cursor: pointer;
  }

  input:focus-visible {
    outline: 2px solid #101820;
    outline-offset: 3px;
  }

  input:disabled,
  input:disabled ~ span {
    cursor: not-allowed;
  }

  input:disabled ~ span,
  input:disabled ~ .product-gift__icon {
    color: #656772;
  }
}

.product-gift__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: #444b51;
}

.product-gift__hint {
  // Aligns under the label text: checkbox 20 + gap 10 + icon 20 + gap 10.
  margin: -8px 0 0;
  padding-left: 60px;
  color: #444b51;
  font-size: 13px;
  line-height: 1.5;
}
</style>
